import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Music } from "lucide-react";

interface KinderliedRow {
  id: string;
  slug: string;
  titel: string;
  kurzbeschreibung: string | null;
  kategorie: string | null;
  saison: string | null;
}

/**
 * Öffentliche Übersichtsseite für gemeinfreie Kinderlieder.
 * SEO-optimiert: jede Karte verlinkt zur Detailseite /kinderlieder/{slug}.
 */
const Kinderlieder = () => {
  const [lieder, setLieder] = useState<KinderliedRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("kinderlieder")
        .select("id, slug, titel, kurzbeschreibung, kategorie, saison")
        .eq("is_published", true)
        .order("sort_order", { ascending: true });

      if (!cancelled) {
        if (error) {
          console.error("[Kinderlieder] Fehler beim Laden:", error);
        }
        setLieder(data ?? []);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Gemeinfreie Kinderlieder mit Noten und Texten",
    itemListElement: lieder.map((l, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://www.musikonzept.de/kinderlieder/${l.slug}`,
      name: l.titel,
    })),
  };

  return (
    <>
      <SEO
        title="Kinderlieder mit Noten & Texten – kostenlos | MusiKonzept"
        description="Gemeinfreie Kinderlieder mit Liedtext, Noten und pädagogischen Tipps für Kita und Grundschule. Kostenlos zum Mitsingen und Lernen."
        path="/kinderlieder"
        jsonLd={itemListJsonLd}
      />

      <main className="container mx-auto px-4 py-12 max-w-6xl">
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Kinderlieder mit Noten & Texten
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Eine wachsende Sammlung gemeinfreier Kinderlieder mit Liedtext,
            Noten und pädagogischen Anregungen für Kita, Krippe und Grundschule.
          </p>
        </header>

        {loading ? (
          <p className="text-center text-muted-foreground">Lieder werden geladen…</p>
        ) : lieder.length === 0 ? (
          <p className="text-center text-muted-foreground">
            Noch keine Lieder veröffentlicht.
          </p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {lieder.map((lied) => (
              <Link key={lied.id} to={`/kinderlieder/${lied.slug}`} className="block group">
                <Card className="h-full transition-shadow group-hover:shadow-lg">
                  <CardHeader>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <Music className="h-4 w-4" />
                      {lied.kategorie ?? "Kinderlied"}
                      {lied.saison ? <span>· {lied.saison}</span> : null}
                    </div>
                    <CardTitle className="group-hover:text-primary transition-colors">
                      {lied.titel}
                    </CardTitle>
                  </CardHeader>
                  {lied.kurzbeschreibung && (
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {lied.kurzbeschreibung}
                      </p>
                    </CardContent>
                  )}
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
    </>
  );
};

export default Kinderlieder;
